/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {EmailTemplateApiService, EmailTemplateModel} from './email-template-api.service';
import {EmailTemplateEditPageComponent} from './email-template-edit-page.component';
import {EmailTemplateRoutePageComponent} from './email-template-route-page.component';

export function emailTemplateStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(EmailTemplateApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageSystem.apiKey)
    && !permissions.hasDataNetworkPermission(PERMISSIONS.dataAdmin.apiKey)
    && !permissions.hasNetworkPermission(PERMISSIONS.politicalAdmin.apiKey, null, 'POLITICAL');
  const rh = adnRouterHelper.createRouterHelper(
    'email-templates.email-template',
    EmailTemplateApiService,
    'app.adm',
    'app/admin'
  );

  states.add(rh.listPath(), rh.list({
    resolve: {
      listReadOnly: readOnly
    },
    views: {
      'content@app': {
        component: EmailTemplateRoutePageComponent,
        bindings: {readOnly: 'listReadOnly'}
      },
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());

  states.add(rh.itemPath(), rh.item({
    resolve: {
      emailTemplatePage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: EmailTemplateModel = {
            id: $stateParams.id,
            objectState: 'ACTIVE',
            validationWarnings: []
          };
          return new SignalAdminResourcePageState(model, true, readOnly(), api, 'Email Template');
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Email Template'));
      },
      model: function(emailTemplatePage: SignalAdminResourcePageState<EmailTemplateModel>) {
        return emailTemplatePage.model();
      }
    },
    views: {
      'content@app': {
        component: EmailTemplateEditPageComponent,
        bindings: {state: 'emailTemplatePage'}
      },
      'context@app': {
        component: SignalAdminResourceContextActionsComponent,
        bindings: {state: 'emailTemplatePage'}
      },
      'title@app': {
        component: SignalAdminResourceTitleComponent,
        bindings: {state: 'emailTemplatePage'}
      }
    }
  }));

  return states.build();
}
